import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import cloneDeep from 'lodash.clonedeep';
import logger from '@widgets/logger';
import Box from '@mui/material/Box';
import LocalizeItemModal from '@features/LocalizeItemModal';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import AddDropdownHeader from '@components/AddDropdownHeader';
import { useTranslation } from '@hooks';
import { micrositesService } from '@services';
import { alertActions } from '@actions';
import { PREVIEW_LOCALES, LANGUAGES_MAP } from '@constants/microsites';
import { validateFileSize } from '@helpers/upload-file';
import { handleApiError } from '@helpers/api';
import { MB } from '@constants/file';
import MicrositeLink from './MicrositeLink';
import { CssMenu } from './styles';

const LinkType = { Link: 0, Survey: 1, File: 2, Microsite: 3, Menu: 4, Form: 5 };

const Landing = ({ isView, locale, selectedMicrosite, linkContext, invalidLinkKeys, showMenuOption }) => {
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [localizeModal, setLocalizeModal] = useState({ open: false, index: null, ctx: null });

    const { setValue, watch } = useFormContext();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const links = watch('links');
    const availableLanguages = LANGUAGES_MAP[locale];
    const currentLocale = PREVIEW_LOCALES[locale];

    const { isBrand, surveysOptions, micrositeOptions, menusOptions, formsOptions } = linkContext;

    const linkTypeOptions = useMemo(() => {
        const opts = [{ value: LinkType.Link, label: 'common.link' }];
        if (!isBrand) {
            opts.push({ value: LinkType.Survey, label: 'common.survey', disabled: !surveysOptions.length });
        }
        opts.push({ value: LinkType.File, label: 'common.file' });
        if (isBrand) {
            opts.push({ value: LinkType.Microsite, label: 'common.microsite', disabled: !micrositeOptions.length });
        }
        if (showMenuOption) {
            opts.push({ value: LinkType.Menu, label: 'common.menu', disabled: !menusOptions.length });
        }
        opts.push({ value: LinkType.Form, label: 'common.form', disabled: !formsOptions.length });
        return opts;
    }, [
        surveysOptions.length,
        micrositeOptions.length,
        menusOptions.length,
        formsOptions.length,
        isBrand,
        showMenuOption,
    ]);

    const linkClick = key => {
        const linksCopy = cloneDeep(links);
        linksCopy[key][0].isOpen = !linksCopy[key][0].isOpen;
        setValue('links', linksCopy, { shouldDirty: true });
    };

    const onClickAdd = type => {
        const linksCopy = cloneDeep(links);
        const newLinks = {
            ...linksCopy,
            [Object.keys(linksCopy).length]: [
                {
                    title: t('common.title'),
                    link: '',
                    language: availableLanguages[0],
                    ...(type === LinkType.Survey && { surveyId: surveysOptions[0]?.value }),
                    ...(type === LinkType.File && { fileUrl: '' }),
                    ...(type === LinkType.Microsite && { linkedMicrositeId: micrositeOptions[0]?.value || '' }),
                    ...(type === LinkType.Menu && { menuId: menusOptions[0]?.value || '' }),
                    ...(type === LinkType.Form && { formId: formsOptions[0]?.value || '' }),
                },
            ],
        };
        setValue('links', newLinks, { shouldDirty: true });
    };

    const onChangeDefaultLink = (e, key, lang) => {
        const linksCopy = cloneDeep(links);
        const linkIndex = linksCopy[key].findIndex(l => l.language === lang);
        linksCopy[key][linkIndex] = { ...linksCopy[key][linkIndex], [e.target.name]: e.target.value };
        setValue('links', linksCopy, { shouldDirty: true });
    };

    const onChangeReference = (e, key, field) => {
        const linksCopy = cloneDeep(links);
        linksCopy[key].forEach(item => {
            item[field] = e.target.value;
            item.link = '';
        });
        setValue('links', linksCopy, { shouldDirty: true });
    };

    const onChangeFile = async (e, key, lang) => {
        const linksCopy = cloneDeep(links);
        const linkIndex = linksCopy[key].findIndex(l => l.language === lang);
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 15 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setIsUploadingFile(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await micrositesService.uploadMicrositeFile(data, selectedMicrosite.id);
            linksCopy[key][linkIndex] = { ...linksCopy[key][linkIndex], fileUrl: response.publicUrl };
            setValue('links', linksCopy, { shouldDirty: true });
        } catch (error) {
            handleApiError(error);
            logger.error("Couldn't upload file microsite");
        } finally {
            setIsUploadingFile(false);
            e.target.value = '';
        }
    };

    const onClickDelete = key => {
        const linksCopy = cloneDeep(links);
        delete linksCopy[key];
        const copyUpdatedIndexes = Object.values(linksCopy).reduce(
            (prev, value, index) => ({ ...prev, [index]: value }),
            {},
        );
        setValue('links', copyUpdatedIndexes, { shouldDirty: true });
    };

    const dndEntries = Object.entries(links);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    margin: '0 -16px',
                    padding: '16px 16px 8px',
                    boxSizing: 'content-box',
                    position: 'sticky',
                    top: -24,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    '@media (max-width: 768px)': {
                        top: 0,
                    },
                }}
            >
                <AddDropdownHeader
                    title={t('MicrositesPage.links')}
                    subtitle={t('MicrositesPage.linksSubtitle')}
                    disabled={isView}
                    options={linkTypeOptions}
                    onAdd={onClickAdd}
                    buttonProps={{ id: 'pl-menu-add-link' }}
                    MenuComponent={CssMenu}
                    menuProps={{
                        slotProps: {
                            paper: { id: 'pl-menu-add-link-list' },
                            list: { 'aria-labelledby': 'basic-button' },
                        },
                    }}
                />
            </Box>
            <div style={{ marginTop: 8, marginBottom: 24 }}>
                <DragAndDrop
                    items={dndEntries.map(i => i[0])}
                    data={links}
                    onDragEndCb={result => {
                        setValue('links', result, { shouldDirty: true });
                    }}
                >
                    {dndEntries.map(([key, links]) => {
                        const link = links.find(l => l.language === currentLocale) || links[0];
                        return (
                            <SortableItem key={key} id={key}>
                                <MicrositeLink
                                    isView={isView}
                                    linkContext={linkContext}
                                    link={link}
                                    isOpen={links[0]?.isOpen}
                                    hasError={invalidLinkKeys.includes(key)}
                                    index={key}
                                    isUploadingFile={isUploadingFile}
                                    linkClick={linkClick}
                                    onChangeDefaultLink={onChangeDefaultLink}
                                    onChangeReference={onChangeReference}
                                    onChangeFile={onChangeFile}
                                    setLocalizeModal={setLocalizeModal}
                                    onClickDelete={onClickDelete}
                                />
                            </SortableItem>
                        );
                    })}
                </DragAndDrop>
            </div>
            {localizeModal.open ? (
                <LocalizeItemModal
                    modal={localizeModal}
                    onClose={() => setLocalizeModal({ open: false, index: null, ctx: null })}
                    currentLocale={currentLocale}
                    propName="link"
                    entity="links"
                    withChangeEntity={
                        !localizeModal?.ctx?.isSurvey &&
                        !localizeModal?.ctx?.isMicrosite &&
                        !localizeModal?.ctx?.isMenu &&
                        !localizeModal?.ctx?.isForm
                    }
                    withUploadFile={localizeModal?.ctx?.isFile}
                    onUploadFile={data => micrositesService.uploadMicrositeFile(data, selectedMicrosite.id)}
                />
            ) : null}
        </>
    );
};

export default Landing;
